import { Inject, Injectable, inject } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { Firestore } from '@angular/fire/firestore';
import {
  DocumentData,
  DocumentReference,
  DocumentSnapshot,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
} from 'firebase/firestore';
import { ADMIN_USER, ADMIN_USER_DEV } from '@smartflip/data-constants';
import { Capacitor } from '@capacitor/core';
import { Customer } from '@smartflip/data-models';

@Injectable({
  providedIn: 'root',
})
export class CustomerService {
  private firestore: Firestore = inject(Firestore);
  private currentCustomer: Subject<Record<string, unknown>> =
    new BehaviorSubject({});
  public currentCustomer$ = this.currentCustomer.asObservable();
  private hasActiveSubscription: Subject<boolean> = new BehaviorSubject(false);
  public hasActiveSubscription$ = this.hasActiveSubscription.asObservable();
  private userId: string = '';

  constructor(
    @Inject('environment')
    private environment: Record<string, unknown>,
  ) {}

  public async getCustomerByUserId(
    userId: string,
  ): Promise<Record<string, unknown>> {
    const path = `customers/${userId}`;
    const customerRef = doc(this.firestore, path);

    const customerSnapshot = await getDoc(customerRef);
    const customer = customerSnapshot.data();
    const subscriptionCollectionQuery = query(
      collection(this.firestore, path, 'subscriptions'),
    );
    const subscriptionData = await getDocs(subscriptionCollectionQuery);
    const subscriptions = subscriptionData.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    const customerWithSubscriptions = {
      ...customer,
      subscriptions,
    };

    this.userId = userId;
    // console.log('customerWithSubscriptions', customerWithSubscriptions);
    this.populateSubscriptions(customerWithSubscriptions);
    this.setActiveSubscriptionStatus(customerWithSubscriptions);

    return customerWithSubscriptions as unknown as Record<string, unknown>;
  }

  private populateSubscriptions(customer: Customer) {
    const { subscriptions } = customer;

    if (subscriptions.length === 0) {
      this.currentCustomer.next(customer);
    } else {
      const productDoc = subscriptions[0]['product'] as DocumentReference;

      getDoc(productDoc)
        .then((product: DocumentSnapshot<unknown | DocumentData>) => {
          this.currentCustomer.next({
            ...customer,
            subscriptions: (subscriptions as Record<string, unknown>[]).map(
              (subscription) => ({
                ...subscription,
                product: product.data(),
              }),
            ),
          });
        })
        .catch((error: string) => {
          console.log(error);
        });
    }
  }

  private setActiveSubscriptionStatus(customer: Customer) {
    let hasSubscription = false;

    if (
      this.environment['free'] ||
      this.userId === ADMIN_USER ||
      this.userId === ADMIN_USER_DEV ||
      Capacitor.isNativePlatform() // TODO: remove once Apple in-app purchases are enabled
    ) {
      hasSubscription = true;
    } else if (customer?.subscriptions?.['length'] > 0) {
      hasSubscription = (
        customer?.subscriptions as Record<string, unknown>[]
      ).reduce((status, sub) => sub['status'] === 'active' || status, false);
    }

    this.hasActiveSubscription.next(hasSubscription);
  }
}
